import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import type { SeriesCreateRequest } from '../../api/types';
import {
  useConfig,
  parseConfig,
  type Team,
  type Player,
} from '../../store/config';
import { Button, Card, Select, TextInput } from '../../ui';
import { FixtureTable, type Fixture } from './FixtureTable';
import { FixtureReview } from './FixtureReview';
import { Link } from 'react-router-dom';

type Preset = {
  id: string;
  name: string;
  version: number;
  settings: SeriesCreateRequest;
};
type TournamentState = {
  revision: number;
  teams: Team[];
  players: Player[];
  fixtures: Fixture[];
  presets: Preset[];
};
type BatchResult = { id: string; error?: string; fixture?: Fixture };

export default function TournamentPanel({
  onManageSeries,
}: {
  onManageSeries?: (id: string) => void;
}) {
  const { config } = useConfig();
  const [browserRoster] = useState(() => {
    try {
      return parseConfig(
        localStorage.getItem('browserRosterBackup') ??
          localStorage.getItem('appConfig'),
      );
    } catch {
      return config;
    }
  });
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ['tournament'],
    queryFn: () => api.get<TournamentState>('/api/v1/tournament'),
    refetchInterval: 5000,
    retry: false,
  });
  const [expanded, setExpanded] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [notice, setNotice] = useState('');
  const [source, setSource] = useState<'teams' | 'csv'>('teams');
  const [selected, setSelected] = useState<string[]>([]);
  const [competition, setCompetition] = useState('Tournament');
  const [pool, setPool] = useState('Group A');
  const [format, setFormat] = useState('bo1');
  const [size, setSize] = useState('5');
  const [double, setDouble] = useState(false);
  const [preset, setPreset] = useState('');
  const [presetName, setPresetName] = useState('');
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<Fixture[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [importPreview, setImportPreview] = useState(false);
  const settings = {
    format,
    players_per_team: Number(size),
    server_region: config.defaultServerRegion,
    game_mode: config.defaultGameMode,
    league_id: config.defaultLeagueId,
    roster_policy: 'organizer_approval',
  };
  const mutation = useMutation({
    mutationFn: async (work: () => Promise<unknown>) => work(),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['tournament'] });
    },
  });
  function generate() {
    mutation.mutate(async () => {
      const result = await api.post<{ fixtures: Fixture[]; errors?: string[] }>(
        '/api/v1/fixtures/preview',
        {
          team_ids: selected.filter((id) =>
            query.data?.teams.some((team) => team.id === id),
          ),
          competition,
          pool,
          double,
          settings,
          preset_id: preset,
          csv: source === 'csv' ? csv : '',
        },
      );
      setPreview(result.fixtures);
      setStep(3);
      setNotice('');
      setErrors(result.errors ?? []);
    });
  }
  function save() {
    mutation.mutate(async () => {
      const result = await api.post<{ results: BatchResult[] }>(
        '/api/v1/fixtures/batch',
        { fixtures: preview },
      );
      const failed = new Set(
        result.results.filter((r) => r.error).map((r) => r.id),
      );
      setErrors(
        result.results.flatMap((r) => (r.error ? [`${r.id}: ${r.error}`] : [])),
      );
      setPreview((current) => current.filter((f) => failed.has(f.id)));
      const saved = result.results.filter((r) => !r.error).length;
      setNotice(
        saved
          ? `${saved} matches added to your schedule. Queue them when your teams are ready.`
          : '',
      );
      if (!failed.size) {
        setExpanded(false);
        setStep(1);
      }
    });
  }
  function action(f: Fixture, action: string) {
    mutation.mutate(() =>
      api.post(`/api/v1/fixtures/${encodeURIComponent(f.id)}/actions`, {
        action,
        revision: query.data?.revision,
      }),
    );
  }
  const teams = query.data?.teams ?? [];
  const chosen = teams.filter((team) => selected.includes(team.id));
  const matchCount =
    ((chosen.length * (chosen.length - 1)) / 2) * (double ? 2 : 1);
  const validSettings =
    competition.trim() &&
    pool.trim() &&
    Number.isInteger(Number(size)) &&
    Number(size) >= 1 &&
    Number(size) <= 5;
  return (
    <Card
      title="Plan your matches"
      description="Pick the teams, review the matchups, then add them to your schedule."
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          variant={expanded ? 'secondary' : 'primary'}
          disabled={mutation.isPending || !query.data}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded
            ? 'Close planner'
            : preview.length || step > 1
              ? 'Continue planning'
              : 'Plan matches'}
        </Button>
        <details className="text-sm">
          <summary className="cursor-pointer py-2 text-ink-dim">
            Import & export
          </summary>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setImportPreview((v) => !v)}>
              Import saved teams
            </Button>
            <Button
              size="sm"
              disabled={mutation.isPending || !query.data}
              onClick={() =>
                mutation.mutate(async () => {
                  const csv = await api.get<string>(
                    '/api/v1/tournament/results.csv',
                  );
                  const url = URL.createObjectURL(
                    new Blob([csv], { type: 'text/csv;charset=utf-8' }),
                  );
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = 'tournament-results.csv';
                  link.click();
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                })
              }
            >
              Export results
            </Button>
          </div>
        </details>
      </div>
      {query.isPending && (
        <p className="mt-4 text-sm text-ink-dim">
          Loading your teams and schedule…
        </p>
      )}
      {query.isError && (
        <p role="alert" className="mt-3 text-sm text-warn">
          Could not load your tournament. {query.error.message}{' '}
          <Button size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </p>
      )}
      {mutation.isError && (
        <p role="alert" className="mt-3 text-sm text-warn">
          {mutation.error.message}
        </p>
      )}
      {notice && (
        <p
          role="status"
          className="mt-4 rounded-lg border border-ok/30 bg-ok/10 p-3 text-sm"
        >
          {notice}
        </p>
      )}
      {importPreview && (
        <div className="mt-4 space-y-3 rounded-lg border border-line p-4">
          <h3 className="font-medium">Bring in teams saved in this browser</h3>
          <p className="text-sm text-ink-dim">
            Share {browserRoster.teams.length} teams and{' '}
            {browserRoster.players.length} players with the other organizers.
            Existing identical teams will be kept.
          </p>
          <p className="text-sm">
            {browserRoster.teams.map((t) => t.name).join(', ') ||
              'No teams saved in this browser yet.'}
          </p>
          <div className="flex gap-2">
            <Button
              disabled={
                mutation.isPending || !query.data || !browserRoster.teams.length
              }
              onClick={() =>
                mutation.mutate(async () => {
                  await api.post('/api/v1/tournament/import', {
                    revision: query.data?.revision,
                    teams: browserRoster.teams,
                    players: browserRoster.players,
                  });
                  setImportPreview(false);
                })
              }
            >
              Import teams
            </Button>
            <Button variant="ghost" onClick={() => setImportPreview(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {expanded && (
        <div className="mt-5 space-y-5 border-t border-line pt-5">
          <ol
            aria-label="Match preparation steps"
            className="grid grid-cols-3 gap-2 text-sm"
          >
            {(
              ['Choose teams', 'Match settings', 'Review matches'] as const
            ).map((label, index) => (
              <li
                key={label}
                aria-current={step === index + 1 ? 'step' : undefined}
                className={`border-b-2 pb-3 ${step === index + 1 ? 'border-gold-300 text-gold-300' : 'border-line text-ink-dim'}`}
              >
                <span className="mr-2 tabular-nums">{index + 1}.</span>
                {label}
              </li>
            ))}
          </ol>
          <fieldset disabled={mutation.isPending} className="space-y-5">
            {step === 1 && (
              <>
                <div>
                  <h3 className="text-lg font-semibold">Who’s playing?</h3>
                  <p className="mt-1 text-sm text-ink-dim">
                    Choose at least two teams. We’ll create one match for every
                    pair.
                  </p>
                </div>
                {teams.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-ink-dim">
                        {chosen.length} teams selected
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setSelected(
                            chosen.length === teams.length
                              ? []
                              : teams.map((t) => t.id),
                          )
                        }
                      >
                        {chosen.length === teams.length
                          ? 'Clear selection'
                          : 'Select all'}
                      </Button>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {teams.map((t) => (
                        <label
                          key={t.id}
                          className={`flex cursor-pointer items-center gap-3 rounded-lg border p-4 ${selected.includes(t.id) ? 'border-gold-400/60 bg-gold-400/10' : 'border-line hover:bg-surface-2'}`}
                        >
                          <input
                            type="checkbox"
                            checked={selected.includes(t.id)}
                            onChange={(e) =>
                              setSelected((ids) =>
                                e.target.checked
                                  ? [...ids, t.id]
                                  : ids.filter((id) => id !== t.id),
                              )
                            }
                          />
                          <span>
                            <span className="block text-sm font-medium">
                              {t.name}
                            </span>
                            <span className="text-xs text-ink-dim">
                              {t.playerSteamIds.length} players
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="space-y-3 rounded-lg border border-dashed border-line p-5">
                    <p className="text-sm">
                      Add your teams first, or import the ones you’ve already
                      saved in this browser.
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                      <Link
                        to="/teams"
                        className="text-sm text-gold-300 underline"
                      >
                        Manage teams
                      </Link>
                      <Button size="sm" onClick={() => setImportPreview(true)}>
                        Import saved teams
                      </Button>
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setSource('csv');
                      setStep(2);
                    }}
                  >
                    Use a CSV instead
                  </Button>
                  <Button
                    variant="primary"
                    disabled={chosen.length < 2 || chosen.length > 32}
                    onClick={() => {
                      setSource('teams');
                      setStep(2);
                    }}
                  >
                    Continue with {chosen.length} teams
                  </Button>
                </div>
                {chosen.length > 32 && (
                  <p role="alert" className="text-sm text-warn">
                    Choose up to 32 teams per group.
                  </p>
                )}
              </>
            )}
            {step === 2 && (
              <>
                <div>
                  <h3 className="text-lg font-semibold">How will they play?</h3>
                  <p className="mt-1 text-sm text-ink-dim">
                    {source === 'teams'
                      ? `${chosen.map((t) => t.name).join(', ')} · ${matchCount} matches planned`
                      : 'Import an existing match schedule from CSV.'}
                  </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="space-y-1 text-sm">
                    Tournament name
                    <TextInput
                      value={competition}
                      onChange={(e) => setCompetition(e.target.value)}
                    />
                  </label>
                  <label className="space-y-1 text-sm">
                    Group name
                    <TextInput
                      value={pool}
                      onChange={(e) => setPool(e.target.value)}
                    />
                  </label>
                  <label className="space-y-1 text-sm">
                    Match format
                    <Select
                      value={format}
                      onChange={(e) => {
                        setFormat(e.target.value);
                        setPreset('');
                      }}
                    >
                      <option value="bo1">One game (BO1)</option>
                      <option value="bo2">Two games (BO2)</option>
                      <option value="bo3">Best of 3</option>
                      <option value="bo5">Best of 5</option>
                    </Select>
                  </label>
                  <label className="space-y-1 text-sm">
                    Players on each team
                    <Select
                      value={size}
                      onChange={(e) => {
                        setSize(e.target.value);
                        setPreset('');
                      }}
                    >
                      {[1, 2, 3, 4, 5].map((n) => (
                        <option key={n} value={n}>
                          {n} vs {n}
                        </option>
                      ))}
                    </Select>
                  </label>
                </div>
                {source === 'teams' && (
                  <label className="flex items-center gap-3 text-sm">
                    <input
                      type="checkbox"
                      checked={double}
                      onChange={(e) => setDouble(e.target.checked)}
                    />
                    Play each opponent twice
                  </label>
                )}
                {source === 'csv' && (
                  <div className="space-y-2">
                    <label className="block text-sm">
                      Paste your CSV
                      <textarea
                        value={csv}
                        onChange={(e) => setCsv(e.target.value)}
                        rows={5}
                        className="mt-2 w-full rounded-lg border border-line bg-surface-2 p-3 font-mono text-xs"
                      />
                    </label>
                    <details className="text-xs text-ink-dim">
                      <summary className="cursor-pointer">
                        CSV columns and team IDs
                      </summary>
                      <p className="my-2 break-words">
                        team_a_id,team_b_id,pool,round,leg,scheduled_at
                      </p>
                      <p>
                        Scheduled times must include a timezone, for example
                        2026-09-07T18:00:00+02:00.
                      </p>
                      {teams.map((t) => (
                        <p key={t.id}>
                          {t.name}: {t.id}
                        </p>
                      ))}
                    </details>
                  </div>
                )}
                <details className="rounded-lg border border-line p-4">
                  <summary className="cursor-pointer text-sm text-ink-dim">
                    Saved settings (optional)
                  </summary>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <label className="space-y-1 text-sm">
                      Use saved settings
                      <Select
                        value={preset}
                        onChange={(e) => {
                          const value = e.target.value;
                          setPreset(value);
                          const chosen = query.data?.presets.find(
                            (p) => p.id === value,
                          );
                          if (chosen) {
                            setFormat(chosen.settings.format);
                            setSize(
                              String(chosen.settings.players_per_team || 5),
                            );
                          }
                        }}
                      >
                        <option value="">Current settings</option>
                        {query.data?.presets.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} · v{p.version}
                          </option>
                        ))}
                      </Select>
                    </label>
                    <label className="space-y-1 text-sm">
                      Save these settings as
                      <TextInput
                        placeholder="e.g. Weekly cup"
                        value={presetName}
                        onChange={(e) => setPresetName(e.target.value)}
                      />
                    </label>
                    <Button
                      size="sm"
                      disabled={!presetName.trim() || !query.data}
                      onClick={() =>
                        mutation.mutate(async () => {
                          await api.post('/api/v1/tournament/presets', {
                            revision: query.data?.revision,
                            preset: {
                              id: crypto.randomUUID(),
                              name: presetName,
                              settings,
                            },
                          });
                          setPresetName('');
                          setNotice('Settings saved for next time.');
                        })
                      }
                    >
                      Save settings
                    </Button>
                  </div>
                </details>
                <div className="flex justify-between gap-3 border-t border-line pt-4">
                  <Button onClick={() => setStep(1)}>Back to teams</Button>
                  <Button
                    variant="primary"
                    disabled={
                      !validSettings ||
                      !query.data ||
                      (source === 'csv' ? !csv.trim() : chosen.length < 2)
                    }
                    loading={mutation.isPending}
                    onClick={generate}
                  >
                    Review matches
                  </Button>
                </div>
              </>
            )}
            {step === 3 && (
              <>
                <FixtureReview
                  fixtures={preview}
                  teams={teams}
                  players={query.data?.players ?? []}
                  onChange={setPreview}
                  disabled={mutation.isPending}
                />
                <div className="flex flex-wrap justify-between gap-3 border-t border-line pt-4">
                  <Button onClick={() => setStep(2)}>Back to settings</Button>
                  <Button
                    variant="primary"
                    disabled={!preview.length}
                    loading={mutation.isPending}
                    onClick={save}
                  >
                    Add {preview.length}{' '}
                    {preview.length === 1 ? 'match' : 'matches'} to schedule
                  </Button>
                </div>
                <p className="text-xs text-ink-dim">
                  Going back and generating a new preview replaces the edits
                  above.
                </p>
              </>
            )}
          </fieldset>
          {errors.length > 0 && (
            <ul role="alert" className="space-y-1 text-sm text-warn">
              {errors.map((error, i) => (
                <li key={i}>{error}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {!expanded && (
        <FixtureTable
          fixtures={query.data?.fixtures ?? []}
          pending={mutation.isPending}
          onAction={action}
          onManageSeries={onManageSeries}
          onQueue={(fixtures) =>
            mutation.mutate(async () => {
              const failures: string[] = [];
              for (const fixture of fixtures) {
                try {
                  const latest =
                    await api.get<TournamentState>('/api/v1/tournament');
                  await api.post(
                    `/api/v1/fixtures/${encodeURIComponent(fixture.id)}/actions`,
                    { action: 'queue', revision: latest.revision },
                  );
                } catch (error) {
                  failures.push(
                    `${fixture.team_a_name} vs ${fixture.team_b_name}: ${(error as Error).message}`,
                  );
                }
              }
              setErrors(failures);
            })
          }
        />
      )}
      {!expanded && errors.length > 0 && (
        <ul role="alert" className="mt-3 text-sm text-warn">
          {errors.map((error, i) => (
            <li key={i}>{error}</li>
          ))}
        </ul>
      )}{' '}
    </Card>
  );
}
